import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

export default function EditableField({
  icon,
  label,
  value,
  placeholder = '',
  inputType = 'text',
  canEdit = true,
  validate,
  onSave,
  renderEdit,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const start = () => {
    setEditedValue(value || '');
    setIsEditing(true);
  };

  const cancel = () => {
    setIsEditing(false);
    setEditedValue('');
  };

  const save = async () => {
    const next = typeof editedValue === 'string' ? editedValue.trim() : editedValue;
    const validationError = validate ? validate(next) : null;
    if (validationError) return;
    setIsSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-sage-50 rounded-2xl border border-sage-100">
      <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-sage-600 shrink-0 shadow-sm">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase font-bold text-sage-500 tracking-wider">{label}</p>
        {isEditing ? (
          renderEdit ? (
            renderEdit({ value: editedValue, setValue: setEditedValue, save, cancel, isSaving })
          ) : (
            <input
              type={inputType}
              autoFocus
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') cancel();
              }}
              disabled={isSaving}
              placeholder={placeholder}
              className="w-full bg-white border border-sage-200 rounded-lg px-2 py-1 text-sm font-bold text-earth-900 focus:ring-2 focus:ring-sage-300 outline-none transition-all disabled:opacity-60"
            />
          )
        ) : (
          <p className="text-sm font-bold text-earth-900 truncate">
            {value || <span className="text-sage-400 italic font-normal">Nije podešeno</span>}
          </p>
        )}
      </div>
      {isEditing ? (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={save}
            disabled={isSaving}
            className="p-2 rounded-lg bg-sage-600 text-white hover:bg-sage-700 hover:scale-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            title="Sačuvaj"
          >
            <Check size={14} />
          </button>
          <button
            onClick={cancel}
            disabled={isSaving}
            className="p-2 rounded-lg bg-white border border-sage-200 text-sage-600 hover:bg-sage-50 hover:scale-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            title="Otkaži"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        canEdit && (
          <button
            onClick={start}
            className="p-2 rounded-lg text-sage-500 hover:text-sage-700 hover:bg-white hover:scale-110 transition-all shrink-0"
            title="Izmeni"
          >
            <Pencil size={14} />
          </button>
        )
      )}
    </div>
  );
}
